import { Search } from 'lucide-react';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Button } from '../../../components/atoms/button/button';
import { DateInput } from '../../../components/atoms/date-input/date-input';
import { Input } from '../../../components/atoms/input/input';
import { Select } from '../../../components/atoms/select/select';
import {
  AvisosFilters,
  StatusAviso,
  STATUS_AVISO_LABEL,
  TipoAviso,
  TIPO_AVISO_LABEL,
} from '../../../types/aviso';

interface Props {
  initialValues: AvisosFilters;
  onApply: (values: AvisosFilters) => void;
  onClear: () => void;
}

const Form = styled.form`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr 1fr auto;
  gap: 10px;
  align-items: end;
  padding: 14px;
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  label {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

export const AvisosFiltersBar = ({
  initialValues,
  onApply,
  onClear,
}: Props) => {
  const [values, setValues] = useState<AvisosFilters>(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const handle = (field: keyof AvisosFilters) => (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setValues((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onApply(values);
  };

  return (
    <Form role="search" onSubmit={handleSubmit}>
      <Field>
        <label htmlFor="avisos-search">Buscar</label>
        <Input
          id="avisos-search"
          placeholder="Título ou mensagem"
          value={values.search ?? ''}
          onChange={handle('search')}
          startIcon={<Search size={14} />}
          data-testid="avisos-search-input"
        />
      </Field>

      <Field>
        <label htmlFor="avisos-tipo">Tipo</label>
        <Select
          id="avisos-tipo"
          value={values.tipo ?? ''}
          onChange={handle('tipo')}
          data-testid="avisos-filter-tipo"
          options={[
            { label: 'Todos', value: '' },
            { label: TIPO_AVISO_LABEL[TipoAviso.GERAL], value: TipoAviso.GERAL },
            {
              label: TIPO_AVISO_LABEL[TipoAviso.FINANCEIRO],
              value: TipoAviso.FINANCEIRO,
            },
          ]}
        />
      </Field>

      <Field>
        <label htmlFor="avisos-status">Status</label>
        <Select
          id="avisos-status"
          value={values.status ?? ''}
          onChange={handle('status')}
          data-testid="avisos-filter-status"
          options={[
            { label: 'Todos', value: '' },
            ...Object.values(StatusAviso).map((s) => ({
              label: STATUS_AVISO_LABEL[s],
              value: s,
            })),
          ]}
        />
      </Field>

      <Field>
        <label htmlFor="avisos-inicio-de">Início ≥</label>
        <DateInput
          id="avisos-inicio-de"
          value={values.dataInicioDe ?? ''}
          onChange={handle('dataInicioDe')}
        />
      </Field>

      <Field>
        <label htmlFor="avisos-inicio-ate">Início ≤</label>
        <DateInput
          id="avisos-inicio-ate"
          value={values.dataInicioAte ?? ''}
          onChange={handle('dataInicioAte')}
        />
      </Field>

      <Actions>
        <Button type="submit" variant="primary" size="sm">
          Filtrar
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setValues({});
            onClear();
          }}
        >
          Limpar
        </Button>
      </Actions>
    </Form>
  );
};
